import { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/contexts/AuthContext";
import {
  Receipt,
  Download,
  CheckCircle,
  Clock,
  AlertCircle,
  Loader2,
  CreditCard,
  Bell,
  Play,
  Send,
  Phone,
  X,
  Link as LinkIcon,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import axios from "axios";
import { loadStripe } from "@stripe/stripe-js";
import { Elements, CardElement, useStripe, useElements } from "@stripe/react-stripe-js";

const API = `${(process.env.REACT_APP_BACKEND_URL || window.location.origin)}/api`;

const statusConfig = {
  paid: { icon: CheckCircle, color: "text-[#10B981]", bg: "bg-[#10B981]/10", label: "Payee" },
  pending: { icon: Clock, color: "text-[#F59E0B]", bg: "bg-[#F59E0B]/10", label: "En attente" },
  overdue: { icon: AlertCircle, color: "text-[#EF4444]", bg: "bg-[#EF4444]/10", label: "En retard" },
};

const reminderLabels = {
  j_minus_3: { label: "J-3", color: "text-[#0099CC]", bg: "bg-[#0099CC]/10" },
  j_day: { label: "Jour J", color: "text-[#F59E0B]", bg: "bg-[#F59E0B]/10" },
  j_plus_7: { label: "J+7", color: "text-[#EF4444]", bg: "bg-[#EF4444]/10" },
};

const CARD_ELEMENT_OPTIONS = {
  style: {
    base: {
      color: "#E2E8F0",
      fontFamily: "'Segoe UI', system-ui, sans-serif",
      fontSize: "16px",
      "::placeholder": { color: "#64748b" },
    },
    invalid: { color: "#EF4444", iconColor: "#EF4444" },
  },
};

function RemindersSection() {
  const [stats, setStats] = useState(null);
  const [reminders, setReminders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [statsRes, remindersRes] = await Promise.all([
        axios.get(`${API}/reminders/stats`),
        axios.get(`${API}/reminders`),
      ]);
      setStats(statsRes.data);
      setReminders(remindersRes.data);
    } catch {
      toast.error("Erreur chargement relances");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const runReminders = async () => {
    setRunning(true);
    try {
      const res = await axios.post(`${API}/reminders/run`);
      const count = res.data.reminders_sent || 0;
      if (count > 0) {
        toast.success(`${count} relance(s) envoyee(s)`);
      } else {
        toast.info("Aucune relance a envoyer aujourd'hui");
      }
      await fetchData();
    } catch {
      toast.error("Erreur execution relances");
    } finally {
      setRunning(false);
    }
  };

  if (loading) {
    return <div className="flex justify-center py-8"><Loader2 className="h-6 w-6 animate-spin text-[#0099CC]" /></div>;
  }

  return (
    <div className="space-y-4" data-testid="reminders-section">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Bell className="h-5 w-5 text-[#0099CC]" />
          <h2 className="text-lg font-semibold text-white">Relances automatiques</h2>
        </div>
        <Button onClick={runReminders} disabled={running}
          className="bg-[#0099CC] hover:bg-[#0088B5] text-white" data-testid="run-reminders-btn">
          {running ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Play className="h-4 w-4 mr-2" />}
          Lancer les relances
        </Button>
      </div>
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <div className="bg-[#151B2B] border border-[#2A3441] rounded-lg p-3 text-center">
            <p className="text-lg font-bold text-white">{stats.total_reminders}</p>
            <p className="text-[10px] text-[#64748b] uppercase">Total envoyees</p>
          </div>
          <div className="bg-[#151B2B] border border-[#2A3441] rounded-lg p-3 text-center">
            <p className="text-lg font-bold text-[#0099CC]">{stats.by_type?.j_minus_3 || 0}</p>
            <p className="text-[10px] text-[#64748b] uppercase">J-3</p>
          </div>
          <div className="bg-[#151B2B] border border-[#2A3441] rounded-lg p-3 text-center">
            <p className="text-lg font-bold text-[#F59E0B]">{stats.by_type?.j_day || 0}</p>
            <p className="text-[10px] text-[#64748b] uppercase">Jour J</p>
          </div>
          <div className="bg-[#151B2B] border border-[#2A3441] rounded-lg p-3 text-center">
            <p className="text-lg font-bold text-[#EF4444]">{stats.by_type?.j_plus_7 || 0}</p>
            <p className="text-[10px] text-[#64748b] uppercase">J+7</p>
          </div>
          <div className="bg-[#151B2B] border border-[#2A3441] rounded-lg p-3 text-center">
            <p className="text-lg font-bold text-[#F59E0B]">{stats.pending_invoices}</p>
            <p className="text-[10px] text-[#64748b] uppercase">Factures pending</p>
          </div>
        </div>
      )}
      {reminders.length > 0 && (
        <div className="bg-[#151B2B] border border-[#2A3441] rounded-xl overflow-hidden">
          <div className="px-4 py-3 border-b border-[#2A3441]">
            <p className="text-sm text-[#94A3B8]">Historique des relances</p>
          </div>
          <div className="divide-y divide-[#2A3441]">
            {reminders.slice(0, 10).map(r => {
              const cfg = reminderLabels[r.reminder_type] || reminderLabels.j_day;
              return (
                <div key={r.id} className="px-4 py-3 flex items-center gap-3" data-testid={`reminder-${r.id}`}>
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center ${cfg.bg}`}>
                    <Send className={`h-3.5 w-3.5 ${cfg.color}`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <Badge className={`${cfg.bg} ${cfg.color} border-0 text-xs`}>{cfg.label}</Badge>
                      <span className="text-white text-sm">{r.invoice_number}</span>
                      <span className="text-[#64748b] text-xs">{r.company_name}</span>
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    <p className="text-sm text-white">{r.amount?.toLocaleString("fr-FR")} EUR</p>
                    <p className="text-xs text-[#64748b]">{new Date(r.sent_at).toLocaleDateString("fr-FR")}</p>
                  </div>
                  <Badge className={r.status === "sent" ? "bg-[#10B981]/10 text-[#10B981] border-0 text-xs" : "bg-[#EF4444]/10 text-[#EF4444] border-0 text-xs"}>
                    {r.status === "sent" ? "Envoyee" : "Echec"}
                  </Badge>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

/* Virtual Terminal Card Form (inside Stripe Elements context) */
function VirtualTerminalForm({ invoice, onSuccess, onCancel }) {
  const stripe = useStripe();
  const elements = useElements();
  const [loading, setLoading] = useState(false);
  const [intentData, setIntentData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    const createIntent = async () => {
      try {
        const res = await axios.post(`${API}/admin/invoices/${invoice.id}/create-payment-intent`);
        setIntentData(res.data);
      } catch (err) {
        setError(err.response?.data?.detail || "Erreur creation PaymentIntent");
      }
    };
    createIntent();
  }, [invoice.id]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements || !intentData) return;
    setLoading(true);
    setError(null);

    const cardElement = elements.getElement(CardElement);
    const { error: stripeError, paymentIntent } = await stripe.confirmCardPayment(
      intentData.client_secret,
      { payment_method: { card: cardElement } }
    );

    if (stripeError) {
      setError(stripeError.message);
      setLoading(false);
      return;
    }

    if (paymentIntent.status === "succeeded") {
      try {
        await axios.post(`${API}/admin/invoices/${invoice.id}/confirm-virtual-payment`, {
          payment_intent_id: paymentIntent.id,
        });
        toast.success("Paiement effectue avec succes !");
        onSuccess();
      } catch (err) {
        setError(err.response?.data?.detail || "Erreur confirmation paiement");
      }
    } else {
      setError(`Statut inattendu: ${paymentIntent.status}`);
    }
    setLoading(false);
  };

  if (error && !intentData) {
    return (
      <div className="text-center py-6">
        <p className="text-[#EF4444] text-sm mb-4">{error}</p>
        <Button onClick={onCancel} variant="outline" className="border-[#2A3441] text-[#94A3B8]">Fermer</Button>
      </div>
    );
  }

  if (!intentData) {
    return (
      <div className="flex justify-center py-8">
        <Loader2 className="h-6 w-6 animate-spin text-[#0099CC]" />
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-5" data-testid="virtual-terminal-form">
      <div className="bg-[#0B0F19] border border-[#2A3441] rounded-lg p-4">
        <div className="flex justify-between mb-1">
          <span className="text-[#94A3B8] text-sm">Facture</span>
          <span className="text-white font-medium">{intentData.invoice_number}</span>
        </div>
        <div className="flex justify-between">
          <span className="text-[#94A3B8] text-sm">Client</span>
          <span className="text-white">{intentData.client_name}</span>
        </div>
        <div className="flex justify-between mt-1">
          <span className="text-[#94A3B8] text-sm">Montant</span>
          <span className="text-[#0099CC] font-bold text-lg">{intentData.amount?.toLocaleString("fr-FR")} EUR</span>
        </div>
      </div>

      <div>
        <label className="text-[#94A3B8] text-sm mb-2 block">Carte bancaire</label>
        <div className="bg-[#0B0F19] border border-[#2A3441] rounded-lg p-4" data-testid="card-element-container">
          <CardElement options={CARD_ELEMENT_OPTIONS} />
        </div>
      </div>

      {error && <p className="text-[#EF4444] text-sm">{error}</p>}

      <div className="flex gap-3">
        <Button type="button" onClick={onCancel} variant="outline" className="flex-1 border-[#2A3441] text-[#94A3B8]">
          Annuler
        </Button>
        <Button
          type="submit"
          disabled={loading || !stripe}
          className="flex-1 bg-[#10B981] hover:bg-[#0D9668] text-white font-semibold"
          data-testid="confirm-vt-payment-btn"
        >
          {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <CreditCard className="h-4 w-4 mr-2" />}
          Encaisser {intentData.amount?.toLocaleString("fr-FR")} EUR
        </Button>
      </div>
    </form>
  );
}

export default function InvoicesPage() {
  const { isAdmin, token } = useAuth();
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [payingId, setPayingId] = useState(null);
  const [sendingLinkId, setSendingLinkId] = useState(null);
  const [vtInvoice, setVtInvoice] = useState(null);
  const [stripePromise, setStripePromise] = useState(null);

  useEffect(() => {
    fetchInvoices();
    if (isAdmin) {
      const pk = process.env.REACT_APP_STRIPE_PUBLIC_KEY;
      if (pk) setStripePromise(loadStripe(pk));
    }
  }, [isAdmin]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("payment") === "success") {
      toast.success("Paiement effectue avec succes ! Merci.");
      window.history.replaceState({}, "", window.location.pathname);
      setTimeout(fetchInvoices, 2000);
    } else if (params.get("payment") === "cancelled") {
      toast.info("Paiement annule.");
      window.history.replaceState({}, "", window.location.pathname);
    }
  }, []);

  const fetchInvoices = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/invoices`);
      setInvoices(response.data);
    } catch {
      console.error("Error fetching invoices");
    } finally {
      setLoading(false);
    }
  };

  const handlePay = async (invoiceId) => {
    setPayingId(invoiceId);
    try {
      const res = await axios.post(`${API}/invoices/${invoiceId}/pay`);
      if (res.data.checkout_url) {
        window.location.href = res.data.checkout_url;
      } else {
        toast.error("Impossible de creer la session de paiement");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur lors du paiement");
    } finally {
      setPayingId(null);
    }
  };

  const handleSendLink = async (invoiceId) => {
    setSendingLinkId(invoiceId);
    try {
      const res = await axios.post(`${API}/admin/invoices/${invoiceId}/send-payment-link`);
      toast.success(`Lien de paiement envoye a ${res.data.email_sent_to}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur envoi lien");
    } finally {
      setSendingLinkId(null);
    }
  };

  const formatDate = (dateStr) => new Date(dateStr).toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" });
  const formatAmount = (amount) => new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR" }).format(amount);

  const totalPaid = invoices.filter(i => i.status === "paid").reduce((s, i) => s + i.amount, 0);
  const totalPending = invoices.filter(i => i.status === "pending" || i.status === "overdue").reduce((s, i) => s + i.amount, 0);
  const totalOverdue = invoices.filter(i => i.status === "overdue").reduce((s, i) => s + i.amount, 0);

  return (
    <div className="space-y-6 animate-fade-in" data-testid="invoices-page">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white">{isAdmin ? "Toutes les Factures" : "Mes Factures"}</h1>
          <p className="text-[#94A3B8] mt-1">{invoices.length} facture{invoices.length > 1 ? "s" : ""}</p>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-[#151B2B] rounded-xl p-5 border border-[#2A3441]">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-9 h-9 rounded-lg bg-[#10B981]/10 flex items-center justify-center">
              <CheckCircle className="h-4 w-4 text-[#10B981]" />
            </div>
            <span className="text-[#94A3B8] text-sm">Payees</span>
          </div>
          <p className="text-xl font-bold text-white">{formatAmount(totalPaid)}</p>
        </div>
        <div className="bg-[#151B2B] rounded-xl p-5 border border-[#2A3441]">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-9 h-9 rounded-lg bg-[#F59E0B]/10 flex items-center justify-center">
              <Clock className="h-4 w-4 text-[#F59E0B]" />
            </div>
            <span className="text-[#94A3B8] text-sm">En attente</span>
          </div>
          <p className="text-xl font-bold text-white">{formatAmount(totalPending)}</p>
        </div>
        <div className="bg-[#151B2B] rounded-xl p-5 border border-[#2A3441]">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-9 h-9 rounded-lg bg-[#EF4444]/10 flex items-center justify-center">
              <AlertCircle className="h-4 w-4 text-[#EF4444]" />
            </div>
            <span className="text-[#94A3B8] text-sm">En retard</span>
          </div>
          <p className="text-xl font-bold text-[#EF4444]">{formatAmount(totalOverdue)}</p>
        </div>
        <div className="bg-[#151B2B] rounded-xl p-5 border border-[#2A3441]">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-9 h-9 rounded-lg bg-[#0099CC]/10 flex items-center justify-center">
              <CreditCard className="h-4 w-4 text-[#0099CC]" />
            </div>
            <span className="text-[#94A3B8] text-sm">Total</span>
          </div>
          <p className="text-xl font-bold text-white">{formatAmount(totalPaid + totalPending)}</p>
        </div>
      </div>

      {/* Reminders Section (Admin Only) */}
      {isAdmin && <RemindersSection />}

      {/* Invoices List */}
      {loading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="h-8 w-8 animate-spin text-[#0099CC]" />
        </div>
      ) : invoices.length === 0 ? (
        <div className="text-center py-20 bg-[#151B2B] rounded-xl border border-[#2A3441]">
          <Receipt className="h-12 w-12 text-[#64748b] mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-white mb-2">Aucune facture</h3>
          <p className="text-[#94A3B8]">Vos factures apparaitront ici</p>
        </div>
      ) : (
        <div className="bg-[#151B2B] rounded-xl border border-[#2A3441] overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-[#0B0F19]">
                  <th className="text-left text-[#94A3B8] text-sm font-medium px-6 py-4">N Facture</th>
                  <th className="text-left text-[#94A3B8] text-sm font-medium px-6 py-4">Description</th>
                  <th className="text-left text-[#94A3B8] text-sm font-medium px-6 py-4">Echeance</th>
                  <th className="text-left text-[#94A3B8] text-sm font-medium px-6 py-4">Statut</th>
                  <th className="text-right text-[#94A3B8] text-sm font-medium px-6 py-4">Montant</th>
                  <th className="text-right text-[#94A3B8] text-sm font-medium px-6 py-4">Actions</th>
                </tr>
              </thead>
              <tbody>
                {invoices.map((invoice) => {
                  const status = statusConfig[invoice.status] || statusConfig.pending;
                  const StatusIcon = status.icon;
                  const isUnpaid = invoice.status === "pending" || invoice.status === "overdue";
                  return (
                    <tr key={invoice.id} className="border-t border-[#2A3441] hover:bg-[#0B0F19]/50" data-testid={`invoice-row-${invoice.id}`}>
                      <td className="px-6 py-4">
                        <span className="text-white font-medium">{invoice.invoice_number}</span>
                      </td>
                      <td className="px-6 py-4 text-[#94A3B8]">{invoice.description}</td>
                      <td className="px-6 py-4 text-[#94A3B8]">{formatDate(invoice.due_date)}</td>
                      <td className="px-6 py-4">
                        <Badge className={`${status.bg} ${status.color} border-0`}>
                          <StatusIcon className="h-3 w-3 mr-1" />{status.label}
                        </Badge>
                      </td>
                      <td className="px-6 py-4 text-right">
                        <span className="text-white font-bold">{formatAmount(invoice.amount)}</span>
                      </td>
                      <td className="px-6 py-4 text-right">
                        <div className="flex items-center justify-end gap-2 flex-wrap">
                          {/* Client: Pay button */}
                          {!isAdmin && isUnpaid && (
                            <Button
                              onClick={() => handlePay(invoice.id)}
                              disabled={payingId === invoice.id}
                              size="sm"
                              className="bg-[#10B981] hover:bg-[#0D9668] text-white text-xs"
                              data-testid={`pay-invoice-${invoice.id}`}
                            >
                              {payingId === invoice.id ? (
                                <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" />
                              ) : (
                                <CreditCard className="h-3.5 w-3.5 mr-1" />
                              )}
                              Payer
                            </Button>
                          )}
                          {/* Admin: Send payment link */}
                          {isAdmin && isUnpaid && (
                            <Button
                              onClick={() => handleSendLink(invoice.id)}
                              disabled={sendingLinkId === invoice.id}
                              size="sm"
                              variant="outline"
                              className="border-[#0099CC]/30 text-[#0099CC] hover:bg-[#0099CC]/10 text-xs"
                              data-testid={`send-link-${invoice.id}`}
                            >
                              {sendingLinkId === invoice.id ? (
                                <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" />
                              ) : (
                                <LinkIcon className="h-3.5 w-3.5 mr-1" />
                              )}
                              Envoyer lien
                            </Button>
                          )}
                          {/* Admin: Virtual Terminal */}
                          {isAdmin && isUnpaid && (
                            <Button
                              onClick={() => setVtInvoice(invoice)}
                              size="sm"
                              variant="outline"
                              className="border-[#10B981]/30 text-[#10B981] hover:bg-[#10B981]/10 text-xs"
                              data-testid={`vt-btn-${invoice.id}`}
                            >
                              <Phone className="h-3.5 w-3.5 mr-1" />
                              TPE Virtuel
                            </Button>
                          )}
                          {/* PDF Download */}
                          <a
                            href={`${API}/invoices/${invoice.id}/pdf`}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid={`download-invoice-${invoice.id}`}
                            className="inline-flex items-center gap-1.5 text-xs text-[#0099CC] hover:text-[#00BBEE] transition-colors bg-[#0099CC]/10 px-3 py-1.5 rounded-lg"
                            onClick={(e) => {
                              e.preventDefault();
                              const link = document.createElement("a");
                              link.href = `${API}/invoices/${invoice.id}/pdf`;
                              link.setAttribute("download", `facture-${invoice.invoice_number}.pdf`);
                              fetch(link.href, { headers: { Authorization: `Bearer ${token}` } })
                                .then(r => r.blob())
                                .then(blob => {
                                  const url = URL.createObjectURL(blob);
                                  link.href = url;
                                  document.body.appendChild(link);
                                  link.click();
                                  document.body.removeChild(link);
                                  URL.revokeObjectURL(url);
                                });
                            }}
                          >
                            <Download className="h-3.5 w-3.5" /> PDF
                          </a>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* TVA Notice */}
      <div className="text-center pt-2">
        <p className="text-xs text-[#64748b]">TVA non applicable, art. 293 B du CGI</p>
      </div>

      {/* Virtual Terminal Modal */}
      {vtInvoice && stripePromise && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm" data-testid="virtual-terminal-modal">
          <div className="bg-[#151B2B] rounded-xl border border-[#2A3441] p-6 w-full max-w-md mx-4">
            <div className="flex items-center justify-between mb-5">
              <h3 className="text-white font-semibold flex items-center gap-2">
                <Phone className="h-5 w-5 text-[#10B981]" />
                TPE Virtuel
              </h3>
              <button
                onClick={() => setVtInvoice(null)}
                className="text-[#64748b] hover:text-white"
                data-testid="close-vt-modal-btn"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <Elements stripe={stripePromise}>
              <VirtualTerminalForm
                invoice={vtInvoice}
                onSuccess={() => {
                  setVtInvoice(null);
                  fetchInvoices();
                }}
                onCancel={() => setVtInvoice(null)}
              />
            </Elements>
          </div>
        </div>
      )}
    </div>
  );
}
